// leadPipelineSnapshot.js

import { differenceInCalendarDays, startOfDay } from 'date-fns';

import {
  readLeadDateField,
  resolveClosedOutcome,
} from 'collection/reports/lead/leadAssigneeWinRates';

/** Open leads at or past this many calendar days since inquiry count as aging. */
export const LEAD_PIPELINE_AGING_DAYS = 30;

/**
 * @param {object|null|undefined} node
 * @returns {boolean}
 */
function isReportableLeadNode(node) {
  if (!node || typeof node !== 'object') return false;
  if (node.isCatalogPlaceholder === true) return false;
  return true;
}

/**
 * Classifies a lead into open / won / lost using dates as source of truth
 * (same rules as assignee win rates).
 *
 * @param {object|null|undefined} node
 * @returns {'open'|'won'|'lost'}
 */
export function classifyLeadPipelineBucket(node) {
  const dateWon = readLeadDateField(node, 'dateWon');
  const dateLost = readLeadDateField(node, 'dateLost');
  const outcome = resolveClosedOutcome(dateWon, dateLost);
  if (outcome?.kind === 'won') return 'won';
  if (outcome?.kind === 'lost') return 'lost';
  return 'open';
}

/**
 * @param {Date|null} inquiryDate
 * @param {Date} [asOf=new Date()]
 * @returns {number|null} Calendar days since inquiry, or null when unknown.
 */
export function daysSinceInquiry(inquiryDate, asOf = new Date()) {
  if (!inquiryDate) return null;
  const days = differenceInCalendarDays(startOfDay(asOf), startOfDay(inquiryDate));
  return Number.isFinite(days) ? Math.max(0, days) : null;
}

/**
 * Builds a current-pipeline snapshot for Lead Overview.
 *
 * @param {object} params
 * @param {object[]} params.nodes
 * @param {Date} [params.asOf]
 * @param {number} [params.agingDays]
 * @returns {object}
 */
export function buildLeadPipelineSnapshot({
  nodes = [],
  asOf = new Date(),
  agingDays = LEAD_PIPELINE_AGING_DAYS,
} = {}) {
  const reportableNodes = (Array.isArray(nodes) ? nodes : []).filter(isReportableLeadNode);
  const threshold = Number.isFinite(agingDays) && agingDays > 0
    ? agingDays
    : LEAD_PIPELINE_AGING_DAYS;

  let openCount = 0;
  let wonCount = 0;
  let lostCount = 0;
  let agingOpenCount = 0;
  let openAgeSum = 0;
  let openAgeSamples = 0;

  reportableNodes.forEach((node) => {
    const bucket = classifyLeadPipelineBucket(node);
    if (bucket === 'won') {
      wonCount += 1;
      return;
    }
    if (bucket === 'lost') {
      lostCount += 1;
      return;
    }

    openCount += 1;
    const ageDays = daysSinceInquiry(readLeadDateField(node, 'dateOfInquiry'), asOf);
    if (ageDays == null) return;
    openAgeSum += ageDays;
    openAgeSamples += 1;
    if (ageDays >= threshold) {
      agingOpenCount += 1;
    }
  });

  const leadCount = reportableNodes.length;
  const closedCount = wonCount + lostCount;
  const ratio = (count) => (leadCount > 0 ? count / leadCount : 0);

  return {
    leadCount,
    openCount,
    wonCount,
    lostCount,
    closedCount,
    agingOpenCount,
    agingDays: threshold,
    avgOpenAgeDays: openAgeSamples > 0 ? openAgeSum / openAgeSamples : null,
    percentOpen: ratio(openCount),
    percentWon: ratio(wonCount),
    percentLost: ratio(lostCount),
  };
}

/**
 * @param {number|null|undefined} ratio
 * @returns {string}
 */
export function formatPipelinePercent(ratio) {
  if (!Number.isFinite(ratio)) return '—';
  return `${Math.round(ratio * 100)}%`;
}

/**
 * @param {number|null|undefined} days
 * @returns {string}
 */
export function formatPipelineDays(days) {
  if (!Number.isFinite(days)) return '—';
  const rounded = Math.round(days);
  return `${rounded} ${rounded === 1 ? 'day' : 'days'}`;
}
